import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { sumDecimals } from '../utils/decimal.utils.js';
import { basePrestationParticipation } from './beneficiaries.types.js';
import { zodQueryBoolean } from './boolean.types.js';
import { zodMoney } from './decimal.types.js';
import { paymentScheduleInputSchema } from './payment-schedules.types.js';
import { TransactionMethodSchema } from './transaction-method.types.js';
export const basePrestationTransaction = {
    include: {
        prestationParticipation: {
            include: {
                ...basePrestationParticipation.include,
                prestation: true,
            },
        },
    },
};
export const baseTransactionAccountMovement = {
    include: {
        financialAccount: true,
        accountingCategories: { select: { id: true, name: true, parentId: true } },
        transactionMatchMemberships: {
            include: {
                transactionMatch: {
                    include: {
                        memberships: {
                            include: {
                                transactionAccountMovement: {
                                    include: {
                                        financialAccount: true,
                                        accountingCategories: { select: { id: true, name: true, parentId: true } },
                                        // Not the member's other lines: a lettrage group spans many écritures, and
                                        // loading each one whole made a single page of 50 écritures weigh 65 MB.
                                        transaction: { include: { journal: true } },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    },
};
export const baseTransaction = {
    include: {
        // Both sides of a cancellation: the contre-passation of a cancelled écriture, and the original
        // when looking at a contre-passation. Needed to link the two in the UI and to tell « annulée »
        // from « est une annulation ».
        cancellationTransaction: { select: { id: true, reference: true, date: true } },
        cancelledTransaction: { select: { id: true, reference: true, date: true } },
        // The business objects an écriture can belong to: the UI must know, like the server, that
        // such an écriture is edited and cancelled from the object and not here.
        immobilisationTransactions: {
            select: { id: true, immobilisationId: true, immobilisation: { select: { name: true } } },
        },
        prestationParticipationSale: { select: { id: true } },
        prestationParticipationCancellation: { select: { id: true } },
        payments: { select: { id: true } },
        rejectedPayment: { select: { id: true } },
        bankDeposit: { select: { id: true, depositSlipNumber: true } },
        bankDepositTransitEntry: { select: { id: true, depositSlipNumber: true } },
        transactionAccountMovements: baseTransactionAccountMovement,
        prestationTransactions: basePrestationTransaction,
        supplierInvoiceAccruals: {
            include: {
                supplier: {
                    select: {
                        name: true,
                    },
                },
                lines: {
                    include: {
                        prestationPrice: true,
                        prestation: true,
                        accountingCategories: { select: { id: true, name: true, parentId: true } },
                    },
                },
            },
        },
        journal: true,
        // The invoices this écriture settles, **through its allocations**: they also carry the share
        // imputed to each, the only correct reading when one cheque settles several.
        supplierInvoiceAllocations: {
            select: {
                id: true,
                amount: true,
                supplierInvoice: {
                    select: {
                        id: true,
                        reference: true,
                        supplier: {
                            select: {
                                name: true,
                            },
                        },
                    },
                },
            },
        },
    },
};
export const basePrestationTransactionFull = {
    include: {
        ...baseTransaction.include,
        // The server refuses to edit or cancel a pointed écriture: the page says where it is pointed.
        transactionAccountMovements: {
            include: {
                ...baseTransactionAccountMovement.include,
                bankReconciliationEntries: {
                    select: { bankReconciliation: { select: { id: true, name: true } } },
                },
            },
        },
        // The captured instruments carried by this écriture. The form prefills from the first one, so
        // the order has to be stable — with an échéancier there are several, and an arbitrary one would
        // show a middle échéance's cheque number as if it were the règlement's.
        payments: {
            orderBy: { id: 'asc' },
            include: { financialAccount: { select: { producesTransferFile: true } } },
        },
        // Present when the règlement was split into instalments — one écriture, N cheques.
        paymentSchedule: { include: { instalments: { orderBy: { number: 'asc' } } } },
    },
};
export const transactionsWithRelations = {
    include: basePrestationTransactionFull.include,
};
// Reports load whole periods: only the fields the generators read, never the shared includes —
// nested payloads of tens of MB on big CSEs have taken the API down.
export const reportTransaction = {
    select: {
        id: true,
        date: true,
        reference: true,
        description: true,
        cancelledAt: true,
        fecImportId: true,
        journal: { select: { code: true } },
        cancelledTransaction: { select: { id: true } },
        prestationParticipationSale: { select: { id: true } },
        prestationParticipationCancellation: { select: { id: true } },
        immobilisationTransactions: { select: { id: true } },
        bankDeposit: { select: { id: true } },
        bankDepositTransitEntry: { select: { id: true } },
        payments: { select: { instrumentNumber: true } },
        rejectedPayment: { select: { instrumentNumber: true } },
        transactionAccountMovements: {
            select: {
                id: true,
                name: true,
                amount: true,
                transactionType: true,
                financialAccount: { select: { id: true, code: true, name: true, accountType: true } },
                accountingCategories: { select: { name: true } },
                transactionMatchMemberships: { select: { transactionMatchId: true } },
            },
        },
        prestationTransactions: {
            select: {
                amount: true,
                prestationParticipation: { select: { prestation: { select: { id: true, name: true } } } },
            },
        },
        supplierInvoiceAccruals: {
            select: {
                type: true,
                supplier: { select: { name: true } },
                lines: {
                    select: {
                        label: true,
                        unitPrice: true,
                        quantity: true,
                        imputedCreditNoteId: true,
                        transactionAccountMovementId: true,
                        prestation: { select: { id: true, name: true } },
                        accountingCategories: { select: { name: true } },
                    },
                },
            },
        },
        supplierInvoiceAllocations: {
            select: { supplierInvoice: { select: { supplier: { select: { name: true } } } } },
        },
    },
};
// The écritures list and the lettrage/pointage pickers only need to know which group a line is
// lettered in; the members of each group stay on the écriture's own page.
export const listTransactionAccountMovement = {
    include: {
        financialAccount: true,
        accountingCategories: { select: { id: true, name: true, parentId: true } },
        transactionMatchMemberships: { select: { id: true, transactionMatchId: true } },
    },
};
export const listTransaction = {
    include: {
        ...baseTransaction.include,
        transactionAccountMovements: listTransactionAccountMovement,
    },
};
export const prestationPaymentDataSchema = z.object({
    prestationParticipationId: z.number(),
    amount: zodMoney(),
    date: zDateField('La date est requise'),
});
// The label qualifies the instruments, it posts nothing: it stays editable when the écriture
// itself no longer is (échéancier, règlement already deposited), through its own endpoint.
export const prestationTransactionLabelSchema = z.object({
    label: z.string().min(1, "L'intitulé est requis"),
});
// Moves a règlement to another mode before it goes into a remise: its écriture's debit line, journal
// and pièce number follow, and so does its instrument in « Règlements à remettre ».
export const changePaymentMethodSchema = z.object({
    financialAccountId: z
        .number({ message: 'Le mode de règlement est requis' })
        .int()
        .positive('Le mode de règlement est requis'),
    instrumentNumber: z
        .union([z.string().min(1), z.literal('').transform(() => undefined)])
        .optional(),
});
export const prestationTransactionDataSchema = z.object({
    employeeId: z.number(),
    financialAccountId: z
        .number({ message: 'Le mode de règlement est requis' })
        .int()
        .positive('Le mode de règlement est requis'),
    // Payment label, required: it qualifies the payment in the « Règlements à remettre » list and
    // makes up for dropping ProWeb's payment sub-types.
    label: z.string().min(1, "L'intitulé est requis"),
    // Expected deposit date. When absent it defaults to the payment date: for a card payment or a
    // transfer both coincide, only cheques and cash are deposited later.
    expectedDepositDate: zDateField('La date de remise prévisionnelle est invalide').optional(),
    // Cheque number or equivalent. Required server-side if the payment method carries instrument
    // numbers (`hasInstrumentNumber`), ignored otherwise. Blank counts as absent rather than as an
    // invalid number: the field only shows for methods that carry one, so switching to a method that
    // doesn't leaves an empty string behind with nowhere left to display a validation error — the
    // submit would fail in silence. The server still refuses a missing number where one is due, and
    // says so.
    instrumentNumber: z
        .union([z.string().min(1), z.literal('').transform(() => undefined)])
        .optional(),
    // Splits this règlement into several instalments, each becoming its own `Payment` in Treasury.
    // Only offered when the payment method carries `supportsEcheancier`; a direct payment is simply a
    // schedule of one, which is why the choice modal the mockup showed was dropped.
    schedule: paymentScheduleInputSchema.optional(),
    prestationPayments: z.array(prestationPaymentDataSchema),
});
export const reglementLineSchema = prestationTransactionDataSchema
    .pick({ financialAccountId: true, instrumentNumber: true, expectedDepositDate: true })
    .extend({ amount: zodMoney() });
// One saisie paid with several modes (100 € espèces + 30 € CB + 20 € chèque): each line becomes its
// own règlement — one mode, one écriture on that mode's journal — and the dossiers are allocated to
// the lines in order, a dossier possibly split across several of them.
export const prestationReglementsDataSchema = prestationTransactionDataSchema
    .omit({ financialAccountId: true, instrumentNumber: true, expectedDepositDate: true })
    .extend({
    reglements: z.array(reglementLineSchema).min(1, 'Au moins un mode de règlement est requis'),
});
const transactionAccountMovementDataSchema = z.object({
    name: z.string().min(1, 'Le libellé est requis'),
    dueDate: zDateField("La date d'échéance est requise"),
    amount: zodMoney(),
    financialAccountId: z.number().min(1, 'Le compte financier est requis'),
    transactionType: z.enum(['debit', 'credit']),
    accountingCategoryIds: z.array(z.number().int()).optional(),
    paymentMethod: TransactionMethodSchema.optional(),
});
const transactionMovementsDataSchema = z
    .array(transactionAccountMovementDataSchema)
    .refine((movements) => movements.some((movement) => movement.transactionType === 'debit') &&
    movements.some((movement) => movement.transactionType === 'credit'), {
    message: 'Au moins un crédit et un débit sont requis',
})
    .refine((movements) => {
    const totalDebit = sumDecimals(movements
        .filter((movement) => movement.transactionType === 'debit')
        .map((movement) => movement.amount));
    const totalCredit = sumDecimals(movements
        .filter((movement) => movement.transactionType === 'credit')
        .map((movement) => movement.amount));
    return totalDebit.equals(totalCredit);
}, {
    message: 'Le total des débits doit être égal au total des crédits',
})
    .refine((movements) => sumDecimals(movements.map((movement) => movement.amount)).gt(0), {
    message: "Le total de l'écriture doit être supérieur à 0",
})
    .refine((movements) => movements.every((movement) => movement.financialAccountId !== 0), {
    message: 'Un compte est requis pour chaque composante',
});
export const transactionDataSchema = z.object({
    journalId: z.number().min(1, 'Le journal est requis'),
    description: z.string().min(1, 'Le libellé est requis'),
    date: zDateField('La date est requise'),
    movements: transactionMovementsDataSchema,
});
export const transactionMatchDataSchema = z.object({
    selectedTransactionMovementIds: z
        .array(z.number().int().positive())
        .min(1, 'Au moins un mouvement supplémentaire doit être sélectionné'),
});
export const transactionsFiltersSchema = z.object({
    journalId: z.coerce.number().optional(),
    accountId: z.coerce.number().optional(),
    accountCodePrefix: z.string().optional(),
    accountingPeriodId: z.coerce.number().optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    onlyValidated: zodQueryBoolean().optional(),
    onlyReconciled: zodQueryBoolean().optional(),
    // Excludes a cancelled écriture **and** its contre-passation. Reserved for screens that offer
    // écritures to act on (bank reconciliation): the écritures list itself must keep showing them —
    // they remain in the books.
    excludeCancelled: zodQueryBoolean().optional(),
});
export const bulkCancelTransactionsSchema = z.object({
    ids: z.array(z.number().int()),
});
export var TransactionUrlHash;
(function (TransactionUrlHash) {
    TransactionUrlHash["CreateInternalTransfer"] = "create-internal-transfer";
})(TransactionUrlHash || (TransactionUrlHash = {}));
// A cancelled écriture stays in the books next to its contre-passation (the two cancel out), so
// the list signals the state instead of hiding either.
export const TRANSACTION_CANCELLATION_STATES = ['cancelled', 'reversal', 'none'];
